/**
 * Pure helpers for the Schedule list (plan 3d): day grouping and the
 * Logged / To log / Planned state label. Kept free of React so the Programs
 * view can reuse them and so they are unit testable.
 */
import { SessionVM } from "./scheduleTypes";
import { getLocalDateString } from "./scheduleDateUtils";

export type SessionStateKey =
  | "live"
  | "logged"
  | "toLog"
  | "planned"
  | "proposed"
  | "rejected";

export interface SessionState {
  key: SessionStateKey;
  label: string;
}

/**
 * State shown on a schedule card. A completed session is "Logged" once it is
 * linked to a training log and "To log" until then; planned sessions stay
 * "Planned" and a running session is "Live".
 */
export const getSessionState = (
  session: Pick<SessionVM, "status" | "linkedTrainingLogID">,
): SessionState => {
  switch (session.status) {
    case "executing":
      return { key: "live", label: "Live" };
    case "complete":
      return session.linkedTrainingLogID
        ? { key: "logged", label: "Logged" }
        : { key: "toLog", label: "To log" };
    case "proposed":
      return { key: "proposed", label: "Proposed" };
    case "rejected":
      return { key: "rejected", label: "Rejected" };
    case "planned":
    default:
      return { key: "planned", label: "Planned" };
  }
};

const parseLocalDate = (plannedDate: string): Date | null => {
  const [y, m, d] = plannedDate.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dayDiff = (from: Date, to: Date): number => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

/**
 * Header for a day group: "Today", "Tomorrow", "Yesterday", or the weekday
 * with its date ("Wednesday 7 Oct"); the year is appended once it differs.
 */
export const getDayGroupLabel = (
  plannedDate: string,
  today: Date = new Date(),
): string => {
  const date = parseLocalDate(plannedDate);
  if (!date) return plannedDate;
  const diff = dayDiff(today, date);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  const base = date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
  return date.getFullYear() === today.getFullYear()
    ? base
    : `${base} ${date.getFullYear()}`;
};

/** Short secondary label for a group header, e.g. "Wed 7 Oct" under "Today". */
export const getDayGroupSubLabel = (
  plannedDate: string,
  today: Date = new Date(),
): string | null => {
  const date = parseLocalDate(plannedDate);
  if (!date) return null;
  const diff = dayDiff(today, date);
  if (Math.abs(diff) > 1) return null;
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export interface DayGroup<T extends Pick<SessionVM, "plannedDate">> {
  /** YYYY-MM-DD */
  date: string;
  label: string;
  subLabel: string | null;
  isToday: boolean;
  sessions: T[];
}

/**
 * Group sessions by `plannedDate`, ascending. Within a day the input order is
 * kept (callers sort by time first when they need it). Sessions that are
 * running (`isLive`) are hoisted into today's group so a live session never
 * hides under an old date.
 */
export const groupSessionsByDay = <
  T extends Pick<SessionVM, "plannedDate"> & Partial<Pick<SessionVM, "isLive">>,
>(
  sessions: T[],
  today: Date = new Date(),
): DayGroup<T>[] => {
  const todayStr = getLocalDateString(today);
  const byDate = new Map<string, T[]>();
  for (const session of sessions) {
    const key =
      session.isLive && session.plannedDate < todayStr
        ? todayStr
        : session.plannedDate;
    const list = byDate.get(key);
    if (list) list.push(session);
    else byDate.set(key, [session]);
  }
  return Array.from(byDate.keys())
    .sort()
    .map((date) => ({
      date,
      label: getDayGroupLabel(date, today),
      subLabel: getDayGroupSubLabel(date, today),
      isToday: date === todayStr,
      sessions: byDate.get(date) as T[],
    }));
};

/** Sort key for sessions inside a day: timed sessions first, by time. */
export const compareSessionsWithinDay = (
  a: Pick<SessionVM, "timeLabel" | "title">,
  b: Pick<SessionVM, "timeLabel" | "title">,
): number => {
  if (a.timeLabel && b.timeLabel) return a.timeLabel.localeCompare(b.timeLabel);
  if (a.timeLabel) return -1;
  if (b.timeLabel) return 1;
  return a.title.localeCompare(b.title);
};
